import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

type Provider = "google" | "linkedin_oidc";

export const SocialAuthButtons = () => {
  const { language } = useLanguage();
  const isAr = language === "ar";
  const [loading, setLoading] = useState<Provider | null>(null);

  const handleOAuth = async (provider: Provider) => {
    try {
      setLoading(provider);
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error("OAuth error:", err);
      toast.error(
        isAr
          ? "تعذّر تسجيل الدخول. تأكد من تفعيل المزود في إعدادات Supabase."
          : "Sign-in failed. Make sure the provider is enabled in Supabase."
      );
      setLoading(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground tracking-widest">
            {isAr ? "أو تابع باستخدام" : "Or continue with"}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => handleOAuth("google")}
        disabled={loading !== null}
        className="w-full inline-flex items-center justify-center gap-3 h-11 px-4 rounded-lg bg-surface border border-border text-foreground text-sm font-semibold hover:bg-surface-muted transition-colors disabled:opacity-60"
      >
        {loading === "google" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.2s2.7-6.2 6-6.2c1.9 0 3.2.8 3.9 1.5l2.7-2.6C17 3.1 14.7 2 12 2 6.9 2 2.8 6.1 2.8 11.2S6.9 20.4 12 20.4c6.9 0 9.5-4.8 9.5-7.3 0-.5-.1-.9-.1-1.3H12z"/>
          </svg>
        )}
        <span>{isAr ? "متابعة باستخدام Google" : "Continue with Google"}</span>
      </button>

      <button
        type="button"
        onClick={() => handleOAuth("linkedin_oidc")}
        disabled={loading !== null}
        className="w-full inline-flex items-center justify-center gap-3 h-11 px-4 rounded-lg bg-surface border border-border text-foreground text-sm font-semibold hover:bg-surface-muted transition-colors disabled:opacity-60"
      >
        {loading === "linkedin_oidc" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" fill="#0A66C2">
            <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.37V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.26 2.37 4.26 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
          </svg>
        )}
        <span>{isAr ? "متابعة باستخدام LinkedIn" : "Continue with LinkedIn"}</span>
      </button>

    </div>
  );
};
